"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { scriptService, type CallScript } from "@/app/_module/services/scriptService";

const SCRIPT_KEYS = {
  all: ["call-scripts"] as const,
  list: () => ["call-scripts", "list"] as const,
};

export const useCallScripts = () => {
  return useQuery<CallScript[]>({
    queryKey: SCRIPT_KEYS.list(),
    queryFn: () => scriptService.getScripts(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateScript = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; content: string; category?: string; productType?: string; isDefault?: boolean; shareWithAgency?: boolean; scope?: string }) =>
      scriptService.createScript(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCRIPT_KEYS.all });
    },
  });
};

export const useUpdateScript = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; content?: string; category?: string; isDefault?: boolean; shareWithAgency?: boolean; scope?: string } }) =>
      scriptService.updateScript(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCRIPT_KEYS.all });
    },
  });
};

export const useDeleteScript = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scriptService.deleteScript(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCRIPT_KEYS.all });
    },
  });
};

export const useRewriteScript = () => {
  return useMutation({
    mutationFn: ({ id, leadData }: { id: string; leadData?: Record<string, any> }) =>
      scriptService.rewriteScript(id, leadData),
  });
};

export const useGenerateScript = () => {
  return useMutation({
    mutationFn: ({ leadData, scriptType, callPhase }: { leadData?: Record<string, any>; scriptType?: string; callPhase?: string }) =>
      scriptService.generateScript(leadData, scriptType, callPhase),
  });
};

export const useDuplicateTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scriptService.duplicateTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCRIPT_KEYS.all });
    },
  });
};
